/**
 * NotificationsPage — Full notification management page.
 *
 * Shows all notifications with ability to mark as read, delete,
 * and navigate to the relevant page.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import {
  markNotificationRead,
  deleteNotification,
  isExternalNotificationLink,
} from '@/services/notificationService';
import { useToast } from '@/contexts/ToastContext';

/** Format a date string to a human-readable relative time or date. */
function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/** Icon and color per notification type. */
function getTypeStyle(type: string): { color: string; label: string } {
  switch (type) {
    case 'buddy_group_invite':
      return { color: 'text-blue-500', label: 'Invitation' };
    case 'buddy_group_invite_accepted':
      return { color: 'text-green-500', label: 'Accepted' };
    case 'buddy_group_invite_declined':
      return { color: 'text-red-500', label: 'Declined' };
    case 'buddy_group_registered':
      return { color: 'text-emerald-500', label: 'Group Entered' };
    default:
      return { color: 'text-muted-foreground', label: 'Notification' };
  }
}

export default function NotificationsPage() {
  const { notifications, unreadCount, markAllRead, refresh } = useNotifications();
  const { showSuccess } = useToast();
  const navigate = useNavigate();
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleMarkRead = async (id: string) => {
    setProcessingId(id);
    try {
      await markNotificationRead(id);
      refresh();
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setProcessingId(id);
    try {
      await deleteNotification(id);
      refresh();
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    showSuccess('All notifications marked as read');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-bold">Notifications</h1>
          {unreadCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-destructive-foreground">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button size="sm" variant="outline" onClick={handleMarkAllRead}>
            <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
            Mark All Read
          </Button>
        )}
      </div>

      {/* Empty state */}
      {notifications.length === 0 && (
        <div className="rounded-lg border p-8 text-center">
          <Bell className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            No notifications yet.
          </p>
        </div>
      )}

      {/* Notification list */}
      {notifications.length > 0 && (
        <div className="divide-y rounded-lg border">
          {notifications.map((n) => {
            const typeStyle = getTypeStyle(n.type);
            const isProcessing = processingId === n.id;

            return (
              <div
                key={n.id}
                className={`flex items-start gap-3 p-4 transition-colors ${
                  n.read ? 'opacity-60' : 'bg-accent/5'
                }`}
              >
                {/* Unread dot */}
                <div className="pt-1.5 shrink-0">
                  {!n.read ? (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  ) : (
                    <div className="h-2 w-2" />
                  )}
                </div>

                {/* Content */}
                <button
                  type="button"
                  onClick={async () => {
                    if (!n.read) {
                      markNotificationRead(n.id).then(() => refresh()).catch(() => {});
                    }
                    if (isExternalNotificationLink(n.linkTo)) {
                      window.open(n.linkTo, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(n.linkTo);
                    }
                  }}
                  className="flex-1 min-w-0 text-left group"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold group-hover:text-primary transition-colors">
                      {n.title}
                    </span>
                    <span className={`text-[10px] font-medium ${typeStyle.color}`}>
                      {typeStyle.label}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {n.body}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {formatTimeAgo(n.createdAt)}
                  </p>
                </button>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0 pt-1">
                  {!n.read && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => handleMarkRead(n.id)}
                      disabled={isProcessing}
                      title="Mark as read"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(n.id)}
                    disabled={isProcessing}
                    title="Delete notification"
                  >
                    {isProcessing && n.read ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
